/**
 * KISSED University · Open Enrollment (iter-18 multi-step intake).
 * Route: /university/enroll
 *
 * Replaces the legacy 2-field minimal flow with a proper four-step UX:
 *   1. Browse — pick a program from the catalog (left panel; right panel is empty).
 *   2. Program Overview — full program intake page (right panel) with all
 *      catalog metadata + a Continue CTA. No payment plan yet.
 *   3. Enrollment Form — expanded Contact Info + Professional Background
 *      sections, prefilled from URL params when arriving from the quiz.
 *   4. Payment Plan + Pay Now — chooses plan and submits; on success the
 *      student is redirected to /university/my-enrollments?fresh=…&pay=…
 *      where the per-enrollment Square checkout CTA lives.
 *
 * Application-gated programs (DTS) short-circuit to /university/apply.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Loader2, GraduationCap, BookOpen, FileText, Stethoscope, CreditCard,
  ArrowRight, ArrowLeft, ShieldCheck, Clock, Layers, Award, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import logo from "@/assets/brand/kissed-logo.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";
import PathwayDiagram from "@/components/PathwayDiagram";

const TYPE_LABEL = {
  certificate: "Certificate",
  residency: "Residency",
  degree: "Degree",
  continuing_education: "Continuing Education",
};
const TYPE_ICON = {
  certificate: BookOpen,
  residency: Stethoscope,
  degree: GraduationCap,
  continuing_education: FileText,
};

const PROFESSION_OPTIONS = [
  "Cosmetology", "Barbering", "Natural Hair", "Esthetics", "Nursing",
  "Medical", "Herbal", "Holistic Health", "Coaching", "Other",
];
const LICENSE_TYPE_OPTIONS = PROFESSION_OPTIONS;

const usd = (n) =>
  n == null || isNaN(n)
    ? "—"
    : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const credentialFor = (p) => {
  if (!p) return "";
  if (p.program_type === "degree") return "Doctor of Trichology Science (DTS)";
  if (p.program_type === "residency") return "Residency Completion Certificate";
  if (p.program_type === "continuing_education") return "CE Certificate of Completion";
  return `${p.title || p.option} Certificate`;
};

const whoFor = (p) => {
  if (!p) return "";
  if (p.program_type === "residency")
    return "Certified trichologists who need hands-on clinical experience and protocol training.";
  if (p.program_type === "degree")
    return "Established trichology professionals pursuing doctoral-level research, teaching, and clinical leadership.";
  if (p.program_type === "continuing_education")
    return "Practicing professionals seeking targeted continuing-education hours and advanced clinical skills.";
  if (/scalp scientist/i.test(p.option || ""))
    return "New students entering the trichology field who want a strong foundational certification.";
  if (/associate trichologist/i.test(p.option || ""))
    return "Foundational-certified students ready to advance into deeper clinical reasoning and applied practice.";
  if (/holistic trichology practitioner/i.test(p.option || ""))
    return "Students completing the full holistic trichology practitioner pathway including theory + clinical work.";
  if (/herbal/i.test(p.option || ""))
    return "Professionals integrating clinical herbal medicine into trichology, wellness, or coaching practice.";
  if (/functional health/i.test(p.option || ""))
    return "Practitioners building root-cause functional health expertise and client-facing protocols.";
  if (/wellness coach/i.test(p.option || ""))
    return "Coaches and wellness professionals adding a credentialed holistic methodology to their toolkit.";
  return "Practicing or aspiring professionals ready to commit to a credentialed pathway.";
};

const learningOutcomes = (p) => {
  const opt = (p?.option || "").toLowerCase();
  const explicit = p?.features || [];
  if (explicit.length) return explicit;
  if (opt.includes("doctor of trichology")) return [
    "Doctoral-level case study reasoning and clinical leadership",
    "Independent research and dissertation completion",
    "Teaching, supervision, and protocol development",
  ];
  if (opt.includes("scalp scientist")) return [
    "Scalp anatomy, microscopy fundamentals, and assessment",
    "Foundational clinical scalp care",
    "Client consultation basics",
  ];
  if (opt.includes("associate trichologist")) return [
    "Advanced microscopy and clinical reasoning",
    "Applied trichology consultation skills",
    "Deeper case analysis",
  ];
  if (opt.includes("holistic trichology practitioner")) return [
    "Full trichology theory and clinical training",
    "Online case-based reasoning",
    "In-person clinical training",
  ];
  if (opt.includes("herbal medicine practitioner")) return [
    "Botanical materia medica and herb–drug interactions",
    "Clinical formulation",
    "Client application and protocol design",
  ];
  if (opt.includes("functional health practitioner")) return [
    "Root-cause functional reasoning",
    "Lab interpretation",
    "Client protocols and follow-through",
  ];
  if (opt.includes("wellness coach")) return [
    "Habit science and coaching methodology",
    "Integrative wellness frameworks",
    "Client-facing accountability practices",
  ];
  return ["Detailed learning outcomes will be published before your cohort start date."];
};

function StepDots({ step }) {
  const labels = ["Browse", "Overview", "Intake", "Payment"];
  return (
    <ol className="flex items-center gap-2 text-xs" data-testid="enroll-stepper">
      {labels.map((l, i) => {
        const idx = i + 1;
        const active = idx === step;
        const done = idx < step;
        return (
          <li key={l} className={`flex items-center gap-1.5 ${active ? "text-purple-900 font-medium" : done ? "text-emerald-700" : "text-slate-400"}`}>
            <span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] ${
              active ? "bg-purple-900 text-white" : done ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"
            }`} data-testid={`enroll-step-${idx}`}>{done ? "✓" : idx}</span>
            <span>{l}</span>
            {idx < labels.length && <span className="text-slate-300 ml-1">→</span>}
          </li>
        );
      })}
    </ol>
  );
}

const DRAFT_KEY = "kcis.enroll.draft.v1";

export default function UniversityEnroll() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState([]);

  // Resume-from-draft (Fix Part 6): restore previous step + program + form
  // from localStorage if present. URL params still override (e.g., quiz redirect).
  const draft = (() => {
    try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch { return null; }
  })();
  const [selected, setSelected] = useState(
    params.get("primary_program") || draft?.selected || null
  );
  const [step, setStep] = useState(
    params.get("primary_program") ? 2 : (draft?.step || 1)
  );
  const [showResumed, setShowResumed] = useState(Boolean(draft?.selected) && !params.get("primary_program"));

  // Quiz / KCIS prefill (Fix 3) — URL params override draft.
  const initial = useMemo(() => ({
    firstName: params.get("first_name") || draft?.form?.firstName || "",
    lastName: params.get("last_name") || draft?.form?.lastName || "",
    email: params.get("email") || draft?.form?.email || "",
    phone: params.get("phone") || draft?.form?.phone || "",
    street: params.get("street_address") || draft?.form?.street || "",
    city: params.get("city") || draft?.form?.city || "",
    state: params.get("state") || draft?.form?.state || "",
    zip: params.get("zip_code") || draft?.form?.zip || "",
    country: params.get("country") || draft?.form?.country || "USA",
    profession: params.get("profession") || draft?.form?.profession || "",
    hasLicense: params.get("has_license") === "true" || !!draft?.form?.hasLicense,
    licenseType: params.get("license_type") || draft?.form?.licenseType || "",
    licenseNumber: params.get("license_number") || draft?.form?.licenseNumber || "",
    licenseState: params.get("license_state") || draft?.form?.licenseState || "",
    recommendedProgram: params.get("recommended_program") || draft?.form?.recommendedProgram || "",
    cameFromQuiz: params.get("from_quiz") === "1" || !!draft?.form?.cameFromQuiz,
  }), [params]); // eslint-disable-line react-hooks/exhaustive-deps
  const [form, setForm] = useState(initial);
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "object" && v?.target ? v.target.value : v }));

  const [plan, setPlan] = useState(draft?.plan || "");
  const [submitting, setSubmitting] = useState(false);

  // Persist draft on every change.
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ selected, step, form, plan }));
    } catch { /* quota */ }
  }, [selected, step, form, plan]);

  useEffect(() => {
    (async () => {
      try {
        const r = await universityApi.options();
        setPricing(r.data?.program_pricing || []);
      } catch (err) {
        toast.error(formatApiError(err, "Failed to load catalog"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const p of pricing) {
      if (!m.has(p.division)) m.set(p.division, []);
      m.get(p.division).push(p);
    }
    return Array.from(m.entries()).map(([division, items]) => ({ division, items }));
  }, [pricing]);

  const selectedProgram = useMemo(
    () => pricing.find((p) => p.option === selected) || null,
    [pricing, selected]
  );

  // Auto-pick the only-allowed plan if applicable.
  useEffect(() => {
    if (!selectedProgram) return;
    const cfg = selectedProgram.config || {};
    setTimeout(() => {
      if (cfg.allows_pay_in_full && !cfg.allows_pay_per_course && !cfg.allows_term_payment) setPlan("pay_in_full");
      else if (!cfg.allows_pay_in_full && cfg.allows_pay_per_course && !cfg.allows_term_payment) setPlan("pay_per_course");
      else if (!cfg.allows_pay_in_full && !cfg.allows_pay_per_course && cfg.allows_term_payment) setPlan("term_payment");
    }, 0);
  }, [selectedProgram]);

  const pickProgram = (option) => {
    setSelected(option);
    setStep(2);
  };

  const submitEnrollment = async (e) => {
    if (e) e.preventDefault();
    if (!selectedProgram) { toast.error("Select a program first."); return; }
    if (!plan) { toast.error("Choose a payment plan."); return; }
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      toast.error("First name, last name, and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await universityApi.enrollmentCreate({
        student_email: form.email.trim(),
        student_name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
        program_key: selectedProgram.option,
        payment_plan: plan,
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        phone: form.phone.trim(),
        street_address: form.street.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        zip_code: form.zip.trim(),
        country: form.country.trim(),
        current_profession: form.profession.trim(),
        has_license: !!form.hasLicense,
        license_type: form.licenseType.trim(),
        license_number: form.licenseNumber.trim(),
        license_state: form.licenseState.trim(),
        came_from_quiz: !!form.cameFromQuiz,
        recommended_program: form.recommendedProgram.trim(),
      });
      const enr = r?.data?.enrollment;
      if (r?.data?.resumed) {
        toast.info("You already have an open enrollment for this program — taking you to it.");
      } else {
        toast.success("Enrollment recorded — please complete tuition payment.");
      }
      // Redirect to My Enrollments with the new enrollment focused and the
      // Pay Now CTA primed.
      // Clear draft on success so future visits start clean.
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      navigate(`/university/my-enrollments?email=${encodeURIComponent(form.email.trim())}&fresh=${enr?.id || "1"}&pay=1`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("requires an application")) {
        toast.error(detail);
      } else {
        toast.error(formatApiError(err, "Could not start enrollment"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="university-enroll" className="min-h-screen bg-[#faf7f5]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/university"><img src={logo} alt="KISSED" className="h-11 w-auto drop-shadow-md" /></Link>
          <span className="font-display text-sm text-slate-700">Open Enrollment</span>
          <div className="ml-auto">
            <Link to="/university/my-enrollments" data-testid="link-my-enrollments" className="text-sm text-purple-900 hover:underline">
              My enrollments →
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-purple-900">Open enrollment is live</div>
            <h1 className="font-display text-3xl sm:text-4xl text-slate-900 mt-2">Choose your pathway</h1>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Certificate, Residency, and CE programs are open enrollment — complete a brief intake
              and pay tuition to activate. Degree pathways (DTS) require an application first.
            </p>
          </div>
          <StepDots step={step} />
        </div>

        {showResumed && selected && (
          <div className="rounded-2xl border border-purple-200 bg-purple-50/70 p-3 text-sm text-purple-900 flex items-center justify-between" data-testid="resume-enrollment-banner">
            <div>
              <span className="font-medium">Resume enrollment:</span>{" "}
              <span>We restored your in-progress enrollment for <span className="font-medium">{selected}</span>. Pick up where you left off.</span>
            </div>
            <button
              type="button"
              data-testid="resume-discard-btn"
              onClick={() => {
                try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
                setSelected(null); setStep(1); setShowResumed(false);
              }}
              className="text-xs underline hover:no-underline"
            >Start over</button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: program list (always visible) */}
            <div className="lg:col-span-2 space-y-8" data-testid="enroll-program-list">
              {grouped.map(({ division, items }) => (
                <section key={division} className="space-y-3">
                  <h2 className="font-display text-lg text-slate-900">{division}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {items.map((p) => {
                      const Icon = TYPE_ICON[p.program_type] || BookOpen;
                      const isSelected = selected === p.option;
                      const gated = p.config?.requires_application;
                      return (
                        <button
                          key={p.option}
                          type="button"
                          onClick={() => pickProgram(p.option)}
                          data-testid={`enroll-program-card-${p.option}`}
                          className={`text-left rounded-2xl border p-4 transition-all bg-white ${
                            isSelected
                              ? "border-purple-700 ring-2 ring-purple-200 shadow"
                              : "border-slate-200 hover:border-purple-300"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-lg bg-purple-50 text-purple-900 flex items-center justify-center shrink-0">
                              <Icon className="w-4 h-4" strokeWidth={2} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[11px] uppercase tracking-wide text-purple-900 font-medium">
                                {TYPE_LABEL[p.program_type] || p.program_type}
                                {gated ? " · Application required" : ""}
                              </div>
                              <div className="font-display text-base text-slate-900 mt-0.5 leading-tight">{p.option}</div>
                              <div className="text-xs text-slate-500 mt-2 flex flex-wrap gap-x-3 gap-y-1">
                                {p.config?.allows_pay_in_full ? <span>Pay In Full {usd(p.tuition_pay_in_full_usd)}</span> : null}
                                {p.config?.allows_pay_per_course && p.per_course_price_usd > 0 ? <span>Per course {usd(p.per_course_price_usd)}</span> : null}
                                {p.config?.allows_term_payment && p.term_count > 0 ? <span>{p.term_count} × {usd(p.per_term_amount_usd)} terms</span> : null}
                                {p.clock_hours ? <span>{p.clock_hours} clock hrs</span> : null}
                              </div>
                              {p.delivery_options?.length ? (
                                <div className="text-[11px] text-purple-900 mt-1">
                                  Delivery: {p.delivery_options.join(" · ")}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>

            {/* Right rail: program overview / intake / payment */}
            <aside className="lg:sticky lg:top-6 h-fit space-y-4" data-testid="enroll-rail">
              {!selectedProgram ? (
                <EmptyRail />
              ) : selectedProgram.config?.requires_application ? (
                <GatedRail program={selectedProgram} />
              ) : step === 2 ? (
                <OverviewRail program={selectedProgram} onContinue={() => setStep(3)} />
              ) : step === 3 ? (
                <IntakeRail form={form} upd={upd} onBack={() => setStep(2)} onContinue={() => setStep(4)} />
              ) : (
                <PaymentRail
                  program={selectedProgram}
                  plan={plan}
                  setPlan={setPlan}
                  onBack={() => setStep(3)}
                  onSubmit={submitEnrollment}
                  submitting={submitting}
                />
              )}
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}

function EmptyRail() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500" data-testid="enroll-rail-empty">
      Pick a program from the list to see a full overview, intake, and payment options.
    </div>
  );
}

function GatedRail({ program }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3" data-testid="enroll-rail-gated">
      <div className="rounded-xl bg-purple-50 border border-purple-100 p-3" data-testid="enroll-selected-summary">
        <div className="text-[11px] uppercase tracking-wide text-purple-900 font-medium">
          {TYPE_LABEL[program.program_type]} · {program.division}
        </div>
        <div className="font-display text-base text-slate-900 mt-1">{program.option}</div>
      </div>
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="enroll-gated-notice">
        <div className="font-medium text-sm flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Application required</div>
        <p className="mt-1">
          This program requires an application + admissions review before enrollment can begin.
          The $50 application fee is paid via Square at submission.
        </p>
        <Link
          to={`/university/apply?primary_program=${encodeURIComponent(program.option)}`}
          className="mt-2 inline-flex items-center gap-1 text-purple-900 hover:underline"
          data-testid="enroll-apply-cta"
        >
          Start your application — $50 Application Fee <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <PathwayDiagram compact />
    </div>
  );
}

function OverviewRail({ program: p, onContinue }) {
  const Icon = TYPE_ICON[p.program_type] || BookOpen;
  const features = learningOutcomes(p);
  const courseCount = p.course_count && p.course_count > 0 ? p.course_count
    : (p.program_type === "continuing_education" ? 1 : (p.features?.length || features.length || 0));
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="enroll-rail-overview">
      <div className="rounded-xl bg-purple-50 border border-purple-100 p-3">
        <div className="text-[11px] uppercase tracking-wide text-purple-900 font-medium flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" /> {TYPE_LABEL[p.program_type]} · {p.division}
        </div>
        <div className="font-display text-lg text-slate-900 mt-1 leading-tight">{p.option}</div>
        {p.label && (
          <div className="text-[11px] text-slate-500 mt-1">Program level: <span className="text-slate-700 font-medium">{p.label}</span></div>
        )}
      </div>

      {p.description && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Short description</div>
          <p className="text-sm text-slate-700 leading-relaxed" data-testid="ov-description">{p.description}</p>
        </div>
      )}

      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Who this program is for</div>
        <p className="text-sm text-slate-700 leading-relaxed" data-testid="ov-who-for">{whoFor(p)}</p>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">What you will learn</div>
        <ul className="space-y-1.5 text-sm text-slate-700" data-testid="ov-outcomes">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-700 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <Tile icon={Clock} label="Clock hours" value={p.clock_hours ? `${p.clock_hours} hrs` : "—"} testid="ov-clock-hours" />
        <Tile icon={Layers} label="Course count" value={String(courseCount)} testid="ov-course-count" />
      </div>

      {p.delivery_options?.length ? (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Delivery options</div>
          <div className="flex flex-wrap gap-1.5" data-testid="ov-delivery">
            {p.delivery_options.map((d) => (
              <span key={d} className="text-[11px] bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5 text-slate-700">{d}</span>
            ))}
          </div>
        </div>
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-1.5 text-sm" data-testid="ov-tuition">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">Tuition</div>
        {p.config?.allows_pay_in_full && (
          <div className="flex justify-between"><span className="text-slate-700">Pay In Full</span><span className="font-medium text-slate-900">{usd(p.tuition_pay_in_full_usd)}</span></div>
        )}
        {p.config?.allows_pay_per_course && p.per_course_price_usd > 0 && (
          <div className="flex justify-between"><span className="text-slate-700">Per course</span><span className="font-medium text-slate-900">{usd(p.per_course_price_usd)}</span></div>
        )}
        {p.config?.allows_term_payment && p.term_count > 0 && (
          <div className="flex justify-between"><span className="text-slate-700">Term payment</span><span className="font-medium text-slate-900">{p.term_count} × {usd(p.per_term_amount_usd)}</span></div>
        )}
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1"><Award className="w-3.5 h-3.5" /> Credential earned</div>
        <p className="text-sm text-slate-700" data-testid="ov-credential">{credentialFor(p)}</p>
      </div>

      <Button className="w-full bg-purple-900 hover:bg-purple-800" onClick={onContinue} data-testid="enroll-overview-continue">
        {p.config?.requires_application ? "Apply First — $50 Application Fee" : "Continue Enrollment"} <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );
}

function Tile({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
        {Icon ? <Icon className="w-3 h-3" /> : null}{label}
      </div>
      <div className="mt-0.5 text-sm font-medium text-slate-900 leading-tight">{value}</div>
    </div>
  );
}

function IntakeRail({ form, upd, onBack, onContinue }) {
  const canContinue = form.firstName.trim() && form.lastName.trim() && form.email.trim();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="enroll-rail-intake">
      <h3 className="font-display text-base text-slate-900">Enrollment intake</h3>

      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Contact information</div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div><Label className="text-xs">First name *</Label><Input data-testid="enr-first-name" value={form.firstName} onChange={upd("firstName")} className="mt-1" /></div>
          <div><Label className="text-xs">Last name *</Label><Input data-testid="enr-last-name" value={form.lastName} onChange={upd("lastName")} className="mt-1" /></div>
          <div className="col-span-2"><Label className="text-xs">Email *</Label><Input data-testid="enr-email" type="email" value={form.email} onChange={upd("email")} className="mt-1" /></div>
          <div className="col-span-2"><Label className="text-xs">Phone</Label><Input data-testid="enr-phone" value={form.phone} onChange={upd("phone")} className="mt-1" /></div>
          <div className="col-span-2"><Label className="text-xs">Street address</Label><Input data-testid="enr-street" value={form.street} onChange={upd("street")} className="mt-1" /></div>
          <div><Label className="text-xs">City</Label><Input data-testid="enr-city" value={form.city} onChange={upd("city")} className="mt-1" /></div>
          <div><Label className="text-xs">State</Label><Input data-testid="enr-state" value={form.state} onChange={upd("state")} className="mt-1" /></div>
          <div><Label className="text-xs">Zip code</Label><Input data-testid="enr-zip" value={form.zip} onChange={upd("zip")} className="mt-1" /></div>
          <div><Label className="text-xs">Country</Label><Input data-testid="enr-country" value={form.country} onChange={upd("country")} className="mt-1" /></div>
        </div>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Professional background</div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div className="col-span-2">
            <Label className="text-xs">Current profession</Label>
            <select
              data-testid="enr-profession"
              value={form.profession}
              onChange={upd("profession")}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            >
              <option value="">Select…</option>
              {PROFESSION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Do you currently hold a professional license?</Label>
            <div className="mt-1 flex gap-2">
              <button
                type="button"
                onClick={() => upd("hasLicense")(true)}
                data-testid="enr-license-yes"
                className={`flex-1 rounded-lg border px-3 py-2 text-sm ${form.hasLicense ? "border-purple-700 bg-purple-50 text-purple-900 font-medium" : "border-slate-300 bg-white text-slate-700"}`}
              >Yes</button>
              <button
                type="button"
                onClick={() => upd("hasLicense")(false)}
                data-testid="enr-license-no"
                className={`flex-1 rounded-lg border px-3 py-2 text-sm ${!form.hasLicense ? "border-purple-700 bg-purple-50 text-purple-900 font-medium" : "border-slate-300 bg-white text-slate-700"}`}
              >No</button>
            </div>
          </div>
          {form.hasLicense && (
            <>
              <div className="col-span-2">
                <Label className="text-xs">License type</Label>
                <select
                  data-testid="enr-license-type"
                  value={form.licenseType}
                  onChange={upd("licenseType")}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
                >
                  <option value="">Select…</option>
                  {LICENSE_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div><Label className="text-xs">License number</Label><Input data-testid="enr-license-number" value={form.licenseNumber} onChange={upd("licenseNumber")} className="mt-1" /></div>
              <div><Label className="text-xs">License state</Label><Input data-testid="enr-license-state" value={form.licenseState} onChange={upd("licenseState")} className="mt-1" /></div>
            </>
          )}
        </div>
      </div>

      {form.cameFromQuiz && (
        <div className="text-[11px] text-purple-900 bg-purple-50 border border-purple-100 rounded-lg px-2 py-1.5" data-testid="enr-quiz-prefill-banner">
          Prefilled from your Admissions Quiz responses. Review and edit any field as needed.
        </div>
      )}

      <div className="flex justify-between gap-2">
        <Button variant="ghost" onClick={onBack} data-testid="enroll-intake-back"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        <Button className="bg-purple-900 hover:bg-purple-800" onClick={onContinue} disabled={!canContinue} data-testid="enroll-intake-continue">
          Continue to payment <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function PaymentRail({ program, plan, setPlan, onBack, onSubmit, submitting }) {
  const cfg = program.config || {};
  return (
    <form onSubmit={onSubmit} className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="enroll-rail-payment">
      <h3 className="font-display text-base text-slate-900">Payment plan</h3>

      <div className="grid grid-cols-1 gap-2">
        {cfg.allows_pay_in_full && (
          <label className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer text-sm ${
            plan === "pay_in_full" ? "border-purple-700 bg-purple-50" : "border-slate-200 hover:border-purple-300"
          }`} data-testid="enroll-plan-pif">
            <input type="radio" name="plan" className="mt-0.5" checked={plan === "pay_in_full"} onChange={() => setPlan("pay_in_full")} />
            <div>
              <div className="font-medium text-slate-900">Pay In Full · {usd(program.tuition_pay_in_full_usd)}</div>
              <div className="text-xs text-slate-500">One-time tuition. Best for full-program commitment.</div>
            </div>
          </label>
        )}
        {cfg.allows_pay_per_course && (
          <label className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer text-sm ${
            plan === "pay_per_course" ? "border-purple-700 bg-purple-50" : "border-slate-200 hover:border-purple-300"
          }`} data-testid="enroll-plan-ppc">
            <input type="radio" name="plan" className="mt-0.5" checked={plan === "pay_per_course"} onChange={() => setPlan("pay_per_course")} />
            <div>
              <div className="font-medium text-slate-900">Pay Per Course · {usd(program.per_course_price_usd)} / course</div>
              <div className="text-xs text-slate-500">Unlock courses as you go. Flexible cadence.</div>
            </div>
          </label>
        )}
        {cfg.allows_term_payment && program.term_count > 0 && (
          <label className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer text-sm ${
            plan === "term_payment" ? "border-purple-700 bg-purple-50" : "border-slate-200 hover:border-purple-300"
          }`} data-testid="enroll-plan-term">
            <input type="radio" name="plan" className="mt-0.5" checked={plan === "term_payment"} onChange={() => setPlan("term_payment")} />
            <div>
              <div className="font-medium text-slate-900">Term Payment · {program.term_count} × {usd(program.per_term_amount_usd)}</div>
              <div className="text-xs text-slate-500">Split tuition across {program.term_count} terms. Total {usd(program.term_count * program.per_term_amount_usd)}.</div>
            </div>
          </label>
        )}
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3 text-xs text-amber-900">
        <div className="font-medium text-sm">Tuition is required to activate your enrollment.</div>
        <p className="mt-1">
          Submitting this form creates your enrollment in <span className="font-medium">Pending Payment</span> status.
          On the next screen you&apos;ll see a <span className="font-medium">Pay Tuition Now</span> button that opens a
          secure Square checkout. You will not have student-portal access until payment is received.
        </p>
      </div>

      <div className="flex justify-between gap-2">
        <Button type="button" variant="ghost" onClick={onBack} data-testid="enroll-payment-back"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        <Button type="submit" className="bg-purple-900 hover:bg-purple-800" disabled={submitting || !plan} data-testid="enroll-submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1" />}
          Submit &amp; continue to payment
        </Button>
      </div>
    </form>
  );
}
